/**
 * National view: National IT's page (DEC-22, DEC-33), in tabs since 5D (DEC-49), one per question National IT brings,
 * with the tab in the address (/national?view=firms) as on Leadership:
 *  - Summary: what waits for National IT, and where the network stands, as structured cards and the hero charts;
 *  - Firms: one firm in detail, the Assessment program, areas by firm and the firm-by-firm table;
 *  - Requests from firms: the requests firms sent, with the answer form;
 *  - Ratings: BTI's Wave 1 rating and National IT's Wave 1 and Wave 2 positions;
 *  - Reviews: the ratings firms sent for National IT's check, and for reviewers the review queue itself.
 * Access is unchanged: the page is National IT's; a Board-only user who opens /national is taken to Leadership's
 * network tab; a reviewer outside National IT keeps the separate review page. Status stays descriptive and red is
 * used only for a committed action plan that is off track.
 */
import {
  BTI_WAVE2_DUE,
  BTI_WAVE2_ITEMS,
  btiPositionCounts,
  DELIVERY_STATUS,
  GOALS,
  longDate,
  nationalWorkLine,
  stateOf,
  SOURCE,
  type LeadershipData,
  type BtiPosition,
  type FirmComparison,
  type Wave2AreaReadiness,
  type Wave2FirmAreas,
  type WorkCount,
} from '@grc/shared/vocabulary';
import { PROGRAM_NAME } from '@grc/shared/leadership-measures';
import { useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useApi, useApiClient, type Loadable } from '../api';
import { Bullet, fullyText, InPlaceKey } from '../charts';
import { AnswerSection, Load, PageBack, PageHeader } from '../components';
import { useFrom } from '../origin';
import { useViewParam, ViewTabs, type ViewTab } from '../tabs';
import type { Me } from '../types';
import { NationalRequestsCard, useNationalQueue, type NationalQueue } from './AskNational';
import { BtiCard, type Measured, type SavePosition } from './Bti';
import { BtiWave2Card } from './BtiWave2';
import { AreasByFirm, BaselineByFirm, CriticalByFirm, FirmByFirmTable } from './FirmComparison';
import { type ProgramFirm } from './Program';
import { NetworkProgram } from './Leadership';
import { DataSourcesTab, SourcesReady } from './DataSources';
import { ReviewQueueTable } from './Review';

interface Summary {
  program: { measure: string; asOf: string | null; firms: ProgramFirm[] };
  participation: { measure: string; firmsInNetwork: number; firmsOnboarded: number; firmsWithCdwResults: number };
  byDomain: { measure: string; domains: FirmComparison['domains'] };
  risks: { measure: string; higherRisksOpen: number; firmsWith: number; firmsRated: number };
  actionPlans: { measure: string; byStatus: Record<string, number> };
  reviews: { measure: string; byStatus: Record<string, number> };
  firms: { measure: string; medians: FirmComparison['medians']; rows: FirmComparison['rows'] };
  assistant: { measure: string; byFirm: Array<{ firmCode: string; turns: number; inputTokens: number; outputTokens: number }> };
}

interface Wave2Item {
  no: number;
  controlId: string;
  measured: Measured | null;
  position: BtiPosition | null;
}

/** GET /national/bti (National IT): each Wave 1 item with today's measure, BTI's comments and National's position, and Wave 2. */
interface NationalBti {
  items: Array<{ no: number; controlId: string; measured: Measured | null; position: BtiPosition | null; response: string | null; evaluatorComment: string | null }>;
  wave2?: { due: string; items: Wave2Item[]; areas?: { areas: Wave2AreaReadiness[]; byFirm: Wave2FirmAreas[] } };
}

/** The National view's tabs, one per question (DEC-49). */
export type NationalTab = 'summary' | 'firms' | 'requests' | 'ratings' | 'reviews' | 'sources';
export const NATIONAL_VIEWS: ReadonlyArray<ViewTab<NationalTab>> = [
  { id: 'summary', label: 'Summary', question: 'Where does the network stand, and what waits for National IT?' },
  { id: 'firms', label: 'Firms', question: `How does each firm compare, and where is it in ${PROGRAM_NAME}?` },
  { id: 'requests', label: 'Requests from firms', short: 'Requests', question: 'Which requests from firms wait for an answer from National IT?' },
  { id: 'ratings', label: 'Ratings', question: "How does Baker Tilly International rate the network, and what is National IT's position?" },
  { id: 'reviews', label: 'Reviews', question: 'Which ratings from firms wait for National IT to check the evidence?' },
  { id: 'sources', label: 'Data sources', short: 'Sources', question: 'Which source files have changed since the last load?' },
];
/**
 * Other names for a tab, so an address in Leadership's words or the old page names still lands on the right tab
 * (rewritten without a history entry): "glance" the Summary, "network" the Firms, "bti" and "wave2" the Ratings,
 * "review" and "queue" the Reviews.
 */
const OTHER_NAMES: Readonly<Record<string, NationalTab>> = { glance: 'summary', network: 'firms', compare: 'firms', bti: 'ratings', wave2: 'ratings', review: 'reviews', queue: 'reviews' };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const pct = (v: number | null | undefined) => (v === null || v === undefined ? 'No results yet' : `${Math.round(v)}%`);

/** A read's state for the work line: loading until it settles, then its count or 'failed'. */
function settled<T>(state: Loadable<T>, count: (d: T) => number): WorkCount | 'loading' {
  if (state.error) return 'failed';
  if (state.loading || !state.data) return 'loading';
  return count(state.data);
}

/** Pick a firm and open its dashboard, controls and risks, read-only (Andres, 2026-09-23). */
function FirmPicker({ firms }: { firms: Array<{ firmCode: string; firmName: string }> }) {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const from = useFrom();
  return (
    <form className="filters firm-picker" onSubmit={(e) => (e.preventDefault(), code && navigate(`/firm/${code}`, { state: from }))}>
      <label>
        See one firm in detail
        <select value={code} onChange={(e) => setCode(e.target.value)}>
          <option value="">Choose a firm…</option>
          {[...firms]
            .sort((a, b) => a.firmName.localeCompare(b.firmName))
            .map((f) => (
              <option key={f.firmCode} value={f.firmCode}>
                {f.firmName}
              </option>
            ))}
        </select>
      </label>
      <button className="pill" disabled={!code}>
        Open the firm's dashboard
      </button>
    </form>
  );
}

type Tone = 'neutral' | 'progress' | 'in_place' | 'attention' | 'off_track';

/**
 * A structured card (5D, DEC-49): its short name (the region's name), the question it answers, the answer as a big
 * figure, a chip that describes the state (never a verdict; red only for an off-track committed plan), and one line
 * saying what the figure means and where it comes from.
 */
function SnapCard({ id, name, question, figure, sub, chart, chip, means, link, source }: {
  /** 5E NEXT PASS 5 (E5): where the figure comes from, in the shared source line under the meaning, never inside it. */
  source: string;
  id: string;
  name: string;
  question: string;
  figure: ReactNode;
  sub?: ReactNode;
  chart?: ReactNode;
  chip: { label: string; tone: Tone };
  means: ReactNode;
  link?: { to: string; label: string };
}) {
  // The shared structured card (components.tsx), in its snapshot look (UX-09).
  return <AnswerSection look="snapshot" id={id} name={name} question={question} answer={figure} sub={sub} chart={chart} chip={chip} meaning={means} source={source} link={link} />;
}

/** A work count as a figure and its state: a count, still loading, or could not be loaded (never a bare zero, SFH-01). */
function workSnap(v: WorkCount | 'loading', none: string, waiting: string): { figure: string; chip: { label: string; tone: Tone } } {
  if (v === 'loading') return { figure: '…', chip: { label: 'Loading', tone: 'neutral' } };
  if (v === 'failed') return { figure: 'Not loaded', chip: { label: 'Could not be loaded', tone: 'neutral' } };
  return v ? { figure: String(v), chip: { label: waiting, tone: 'attention' } } : { figure: 'None', chip: { label: none, tone: 'neutral' } };
}

/** A network median as a headline: in place (fully or partly), the fully share beside it, and the split as a bar. */
function MedianSnap({ id, name, question, median, fully, firms, of, goal, source }: {
  id: string;
  name: string;
  question: string;
  median: number | null;
  fully: number | null | undefined;
  firms: number;
  of: string;
  goal: string;
  source: string;
}) {
  const partly = median !== null && fully !== null && fully !== undefined ? Math.max(0, median - fully) : null;
  // PL-15: the same state words as Leadership's cards (stateOf), never a separate wording.
  const chip = stateOf(median);
  return (
    <SnapCard
      id={id}
      name={name}
      question={question}
      figure={pct(median)}
      sub={median !== null && partly !== null ? `in place, ${fullyText(median, partly)}` : median !== null ? 'in place' : undefined}
      chart={median !== null ? <Bullet pct={median} partly={partly} label={`${name}: ${pct(median)} in place${partly !== null ? `, ${fullyText(median, partly)}` : ''}`} /> : undefined}
      chip={chip}
      means={
        <>
          The typical firm (median of {plural(firms, 'firm', 'firms')} with CDW results): share of {of} in place. Goal: {goal}.
        </>
      }
      source={source}
    />
  );
}

/** Summary: what waits for National IT, then the network's headline figures and the hero charts. */
function SummaryTab({ s, open, reviews, wave2Recorded, reviewer }: { s: Loadable<Summary>; open: WorkCount | 'loading'; reviews: WorkCount | 'loading'; wave2Recorded: WorkCount | 'loading'; reviewer: boolean }) {
  const req = workSnap(open, 'Nothing waiting', 'Waiting for an answer');
  const rev = workSnap(reviews, 'Nothing waiting', 'Waiting for a check');
  const w2: { figure: string; chip: { label: string; tone: Tone } } =
    wave2Recorded === 'loading'
      ? { figure: '…', chip: { label: 'Loading', tone: 'neutral' } }
      : wave2Recorded === 'failed'
        ? { figure: 'Not loaded', chip: { label: 'Could not be loaded', tone: 'neutral' } }
        : {
            figure: `${wave2Recorded} of ${BTI_WAVE2_ITEMS.length}`,
            chip: wave2Recorded >= BTI_WAVE2_ITEMS.length ? { label: 'Every item recorded', tone: 'in_place' } : wave2Recorded ? { label: 'Recording under way', tone: 'progress' } : { label: 'Not started', tone: 'neutral' },
          };
  return (
    <>
      {/* National IT's own work first (DEC-33), from its own reads: a slow or failed summary never hides it. */}
      <section aria-labelledby="national-work-h" className="national-band national-work">
        <h3 id="national-work-h" className="national-band-title">
          What is waiting on National IT?
        </h3>
        {open === 'loading' || wave2Recorded === 'loading' || reviews === 'loading' ? (
          <p className="lead-headline muted" role="status">
            Loading what waits for National IT…
          </p>
        ) : (
          <p className="lead-headline">{nationalWorkLine(open, reviews, wave2Recorded)}</p>
        )}
        <div className="cards snaps">
          <SnapCard
            id="snap-requests"
            name="Requests from firms"
            question="How many requests from firms wait for an answer?"
            figure={req.figure}
            chip={req.chip}
            means="Open requests firms sent through the National agent when it could not answer clearly."
            source="Ask National"
            link={{ to: '/national?view=requests', label: 'Answer requests from firms' }}
          />
          <SnapCard
            id="snap-ratings"
            name="Ratings to review"
            question="How many ratings from firms wait for National IT's check?"
            figure={rev.figure}
            chip={rev.chip}
            means="Ratings firms sent to National IT through the control assistant, waiting for a check of the evidence."
            source="the review queue"
            // CP-09: only a reviewer opens the queue; National IT outside the group sees the counts.
            link={{ to: '/national?view=reviews', label: reviewer ? 'Open the review queue' : 'See ratings to review' }}
          />
          <SnapCard
            id="snap-wave2"
            name="BTI Wave 2 positions"
            question="On how many BTI Wave 2 requirements has National IT recorded its position?"
            figure={w2.figure}
            chip={w2.chip}
            means={`National IT's own position on each Wave 2 requirement before BTI's submission date, ${longDate(BTI_WAVE2_DUE)}.`}
            source="National IT's positions in this portal"
            link={{ to: '/national?view=ratings', label: 'Record National IT’s positions' }}
          />
        </div>
      </section>
      <SourcesReady />
      <Load state={s} what="the national view">
        {(d) => {
          const c: FirmComparison = { measure: d.firms.measure, domains: d.byDomain.domains, medians: d.firms.medians, rows: d.firms.rows };
          const cdw = d.participation.firmsWithCdwResults;
          const plansTotal = Object.values(d.actionPlans.byStatus).reduce((a, b) => a + b, 0);
          const off = d.actionPlans.byStatus.off_track ?? 0;
          const atRisk = d.actionPlans.byStatus.at_risk ?? 0;
          const turns = d.assistant.byFirm.reduce((a, f) => a + f.turns, 0);
          const source = "CDW, or National IT's accepted review when newer";
          return (
            <>
              <section aria-labelledby="network-status-h" className="national-band">
                <h3 id="network-status-h" className="national-band-title">
                  Where does the network stand?
                </h3>
                <div className="cards snaps">
                  <MedianSnap id="snap-btc" name="BTC Baseline" question="How much of the BTC Baseline does the typical firm have in place?" median={c.medians.btc} fully={c.medians.fully?.btc} firms={cdw} of="the 56 BTC Baseline controls" goal={GOALS.btc} source={source} />
                  <MedianSnap
                    id="snap-bti"
                    name="BTI Baseline"
                    question="How much of the BTI Baseline does the typical firm have in place?"
                    median={c.medians.bti}
                    fully={c.medians.fully?.bti}
                    firms={cdw}
                    of="the 39 BTI requirements (Waves 1 and 2)"
                    goal={`every requirement; Wave 2 goes to BTI ${longDate(BTI_WAVE2_DUE)}`}
                    source={source}
                  />
                  <MedianSnap id="snap-critical" name="Critical protections" question="How many critical protections does the typical firm have in place?" median={c.medians.criticalPct} fully={c.medians.fully?.criticalPct} firms={cdw} of="the controls with Critical priority" goal={GOALS.critical} source={source} />
                  <SnapCard
                    id="snap-firms"
                    name="Firms with results"
                    question="How many member firms have CDW assessment results?"
                    figure={`${cdw} of ${d.participation.firmsInNetwork}`}
                    chip={cdw >= d.participation.firmsInNetwork ? { label: 'Every firm assessed', tone: 'in_place' } : { label: 'Assessments under way', tone: 'progress' }}
                    means={`${d.participation.measure}.`}
                    source="CDW assessment results loaded in this portal"
                    link={{ to: '/national?view=firms', label: 'See each firm' }}
                  />
                  <SnapCard
                    id="snap-risks"
                    name="Higher risks"
                    question="How many higher risks have no response yet?"
                    figure={`${d.risks.higherRisksOpen} open`}

                    chip={d.risks.higherRisksOpen ? { label: 'Responses to record', tone: 'attention' } : { label: 'None open', tone: 'in_place' }}
                    means={`At ${d.risks.firmsWith} of ${d.risks.firmsRated} firms with a CDW risk register. ${d.risks.measure.replace(/^Assessed by CDW, 2026: /, '').replace(/^./, (x) => x.toUpperCase())}.`}
                    source={SOURCE.register}
                  />
                  <SnapCard
                    id="snap-plans"
                    name="Action plans off track"
                    question="Are the firms' committed action plans on track?"
                    figure={off ? `${off} of ${plansTotal}` : `None of ${plansTotal}`}
                    sub="off track"
                    chip={off ? { label: DELIVERY_STATUS.off_track!.label, tone: 'off_track' } : atRisk ? { label: `${atRisk} ${DELIVERY_STATUS.at_risk!.label.toLowerCase()}`, tone: 'attention' } : { label: DELIVERY_STATUS.on_track!.label, tone: 'progress' }}
                    means={`Goal: ${GOALS.plans.toLowerCase()}.`}
                    source={`${SOURCE.plans} (${d.actionPlans.measure.replace(/^Reported by the firm: /, '')})`}
                  />
                </div>
                <div className="cards two">
                  <BaselineByFirm c={c} which="btc" level={3} source={SOURCE.cdw} />
                  <BaselineByFirm c={c} which="bti" level={3} source={SOURCE.cdw} />
                </div>
                <CriticalByFirm c={c} level={3} source={SOURCE.cdw} />
              </section>

              <section aria-labelledby="national-usage-h" className="national-band">
                <h3 id="national-usage-h" className="national-band-title">
                  How are the assistants used?
                </h3>
                <div className="cards snaps">
                  <SnapCard
                    id="snap-assistant"
                    name="AI assistants this month"
                    question="How much did firms use the assistants this month?"
                    figure={turns ? `${turns} answers` : 'No answers yet'}
                    sub={turns ? `for ${plural(d.assistant.byFirm.length, 'firm', 'firms')}, ${Math.round(d.assistant.byFirm.reduce((a, f) => a + f.inputTokens + f.outputTokens, 0) / 1000).toLocaleString('en-CA')} thousand tokens` : undefined}
                    chip={{ label: 'This month', tone: 'neutral' }}
                    means={`${d.assistant.measure}.`}
                    source="the portal's usage records"
                  />
                </div>
              </section>
            </>
          );
        }}
      </Load>
    </>
  );
}

/**
 * Firms: one firm in detail, the assessment program, areas by firm and every firm in one table. DEC-55: the program is
 * the DEC-49 program table Leadership shows (NetworkProgram, from Leadership's own read of the network), not a copy.
 */
function FirmsTab({ s }: { s: Loadable<Summary> }) {
  const lead = useApi<LeadershipData>('/leadership');
  return (
    <Load state={s} what="the firms">
      {(d) => {
        const c: FirmComparison = { measure: d.firms.measure, domains: d.byDomain.domains, medians: d.firms.medians, rows: d.firms.rows };
        return (
          <>
            <FirmPicker firms={d.firms.rows} />
            <div className="lead-grid">
              <Load state={lead} what={PROGRAM_NAME}>
                {(l) => <NetworkProgram n={l.network} />}
              </Load>
            </div>
            <AreasByFirm c={c} level={3} source={SOURCE.cdw} />
            <FirmByFirmTable c={c} level={3} source={SOURCE.cdw} />
          </>
        );
      }}
    </Load>
  );
}

/** Ratings: BTI's Wave 1 rating with its comments and National IT's positions, then Wave 2 before its due date. */
function RatingsTab({ bti, onSave }: { bti: Loadable<NationalBti>; onSave: (wave: 'wave1' | 'wave2') => SavePosition }) {
  return (
    // Loading and a failed read are said, with Try again; never a reduced card that looks complete (SFH-02).
    <Load state={bti} what="BTI's Wave 1 rating">
      {(d) => (
        <>
          <BtiCard
            headingLevel={3}
            showDue={false}
            measured={d.items.flatMap((i) => (i.measured ? [i.measured] : []))}
            comments={Object.fromEntries(d.items.map((i) => [i.no, { response: i.response, evaluatorComment: i.evaluatorComment }]))}
            positions={d.items.flatMap((i) => (i.position ? [i.position] : []))}
            onSavePosition={onSave('wave1')}
          />
          {d.wave2 ? (
            <BtiWave2Card
              headingLevel={3}
              measured={d.wave2.items.flatMap((i) => (i.measured ? [i.measured] : []))}
              areas={d.wave2.areas}
              positions={d.wave2.items.flatMap((i) => (i.position ? [i.position] : []))}
              onSavePosition={onSave('wave2')}
            />
          ) : (
            <p role="alert" className="notice">
              BTI Wave 2 could not be loaded.
            </p>
          )}
        </>
      )}
    </Load>
  );
}

/** Reviews: the counts by review state, then the queue itself for National IT reviewers (DEC-35: what is reviewed is a rating). */
function ReviewsTab({ s, reviewer }: { s: Loadable<Summary>; reviewer: boolean }) {
  return (
    <>
      <Load state={s} what="ratings to review">
        {(d) => {
          const b = d.reviews.byStatus;
          const waiting = b.submitted ?? 0;
          return (
            <section className="card snap snap-wide" aria-labelledby="ratings-to-review">
              <h3 id="ratings-to-review">Ratings to review</h3>
              <p className="snapshot-stat">
                {waiting ? (
                  <>
                    <strong>{waiting}</strong> {waiting === 1 ? 'rating waits' : 'ratings wait'} for National IT review
                  </>
                ) : (
                  'No ratings wait for National IT review'
                )}
              </p>
              <p className="muted small">
                {/* Nothing is said as a bare zero: "none accepted yet", "none returned". */}
                {d.reviews.measure}: {b.accepted ? `${b.accepted} accepted` : 'none accepted yet'}, {b.returned ? `${b.returned} returned to the firm with questions` : 'none returned to the firm with questions'}.
              </p>
              {!reviewer && <p className="muted small">National IT reviewers record the decisions in the review queue.</p>}
            </section>
          );
        }}
      </Load>
      {/* The queue reads /review/submissions, which only the reviewer group may call (assertReviewer): the same rule as the old page. */}
      {reviewer && (
        <section aria-labelledby="review-queue-h" className="national-band">
          <h3 id="review-queue-h" className="national-band-title">
            The review queue
          </h3>
          <p className="muted small">Ratings firms sent to National IT, waiting for a check of the evidence. The firm owns the result.</p>
          <ReviewQueueTable />
        </section>
      )}
    </>
  );
}

function NationalView({ reviewer }: { reviewer: boolean }) {
  const { view, setView, liveView } = useViewParam(NATIONAL_VIEWS, 'summary', OTHER_NAMES);
  const state = useApi<Summary>('/national/summary');
  const queue: Loadable<NationalQueue> = useNationalQueue();
  const bti = useApi<NationalBti>('/national/bti');
  const call = useApiClient();
  const saveTo = (wave: 'wave1' | 'wave2'): SavePosition => async (itemNo, input) => {
    const saved = await call<BtiPosition>(`/national/bti/${wave}/${itemNo}`, { method: 'PUT', body: input });
    bti.reload();
    return saved;
  };
  // A response without Wave 2 has no positions to count: said as could not be loaded, never as zero.
  const wave2Read = settled(bti, (d) => (d.wave2 ? BTI_WAVE2_ITEMS.length - btiPositionCounts(d.wave2.items.flatMap((i) => (i.position ? [i.position] : [])), 2).not_set : -1));
  const wave2Recorded: WorkCount | 'loading' = wave2Read === -1 ? 'failed' : wave2Read;
  const open = settled(queue, (d) => d.requests.filter((r) => r.status === 'open').length);
  // The reviews count comes from the summary; a slow or failed summary is said in the line, as SFH-01 does.
  const reviews = settled(state, (s) => s.reviews.byStatus.submitted ?? 0);
  const question = NATIONAL_VIEWS.find((v) => v.id === view)!.question;
  return (
    <>
      {/* The lede says what National IT does here, not how the page is laid out; the title needs no eyebrow (N4). */}
      <PageBack to="/">Back to Home</PageBack>
      <PageHeader title="National view" lede="Answer requests from firms, review the ratings they send, and record National IT's BTI Wave 2 positions; then see every firm side by side." />
      <ViewTabs views={NATIONAL_VIEWS} view={view} onSelect={setView} liveView={liveView} label="National view sections" idPrefix="national" />
      <div id="national-panel" role="tabpanel" aria-labelledby={`national-tab-${view}`} tabIndex={0} className="lead-panel">
        <div className="lead-question-row">
          <h2 className="lead-question">{question}</h2>
          {(view === 'summary' || view === 'firms') && <InPlaceKey median={view === 'summary'} />}
        </div>
        {view === 'summary' && <SummaryTab s={state} open={open} reviews={reviews} wave2Recorded={wave2Recorded} reviewer={reviewer} />}
        {view === 'firms' && <FirmsTab s={state} />}
        {view === 'requests' && <NationalRequestsCard state={queue} />}
        {view === 'ratings' && <RatingsTab bti={bti} onSave={saveTo} />}
        {view === 'reviews' && <ReviewsTab s={state} reviewer={reviewer} />}
        {view === 'sources' && <DataSourcesTab />}
      </div>
    </>
  );
}

/**
 * National IT only. A Board-only user is taken to Leadership's network tab, which carries the same firm-by-firm
 * comparison (DEC-22); a reviewer outside National IT to the review page they always had (with its filter); anyone
 * else Home. A user in both the National IT and Board groups keeps this page (DEC-33).
 */
export function National() {
  const me = useOutletContext<Loadable<Me>>();
  const [params] = useSearchParams();
  const asked = params.get('view');
  return (
    <Load state={me} what="your access">
      {(m) => {
        // Decided before NationalView mounts, so no one outside National IT fires three refused reads and a Try again
        // that cannot work (CP-01).
        if (m.tiers.national) return <NationalView reviewer={Boolean(m.tiers.reviewer)} />;
        const status = params.get('status');
        const reviewPage = `/review${status ? `?status=${encodeURIComponent(status)}` : ''}`;
        // A reviewer who asked for the Reviews tab gets the review page, even when also in the Board group.
        if (m.tiers.reviewer && (asked === 'reviews' || (asked && OTHER_NAMES[asked] === 'reviews'))) return <Navigate to={reviewPage} replace />;
        return <Navigate to={m.tiers.board ? '/leadership?view=network' : m.tiers.reviewer ? reviewPage : '/'} replace />;
      }}
    </Load>
  );
}
